import { lazy, Suspense, useEffect } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { ArrowLeft } from "lucide-react"
import { SignIn, SignUp } from "@clerk/react"
import { useAuth, AUTH_PROVIDER } from "../contexts/AuthContext"
import { clerkAppearance } from "../contexts/ClerkAuthProvider"
import PageMeta from "./PageMeta"

const LegacyLoginPage = lazy(() => import("./LegacyLoginPage"))

// Sign in / sign up / Google / forgot password, all through Clerk's prebuilt components (WO-15).
// /login renders <SignIn>, /signup renders <SignUp>; both route their own steps under the path.
// Username, display name, location and currency are collected afterwards on /profile-setup.
function ClerkLoginPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, profile, loading, accountError, authUnavailable, clerkSignedIn, loadUserProfile, signOut } = useAuth()
  const isSignUp = location.pathname.startsWith("/signup")

  // If already logged in, redirect away
  useEffect(() => {
    if (loading) return
    if (user && profile?.username) {
      navigate('/', { replace: true })
    } else if (user && !profile?.username) {
      navigate('/profile-setup', { replace: true })
    }
  }, [user, profile, loading, navigate])

  let content
  if (authUnavailable) {
    content = (
      <div className="p-3 rounded-lg text-sm bg-red-500/10 text-red-400 border border-red-500/20">
        Sign-in is temporarily unavailable. Browsing and prices still work; please check back soon.
      </div>
    )
  } else if (clerkSignedIn && accountError) {
    content = (
      <div className="space-y-4">
        <div className="p-3 rounded-lg text-sm bg-red-500/10 text-red-400 border border-red-500/20">{accountError}</div>
        <button
          type="button"
          onClick={() => loadUserProfile()}
          className="w-full bg-purple text-cream hover:bg-purple/80 transition-colors py-2 rounded-lg font-medium"
        >
          Try again
        </button>
        <button
          type="button"
          onClick={() => signOut()}
          className="w-full text-cream text-sm hover:text-purple transition-colors"
        >
          Sign out
        </button>
      </div>
    )
  } else if (loading && !clerkSignedIn) {
    content = <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple" aria-label="Loading sign-in"></div>
  } else if (isSignUp) {
    content = <SignUp routing="path" path="/signup" signInUrl="/login" appearance={clerkAppearance} />
  } else {
    content = <SignIn routing="path" path="/login" signUpUrl="/signup" appearance={clerkAppearance} />
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-dark p-4">
      <PageMeta title="Log in | Lilycove" description="Log in or create your Lilycove account." />
      {/* Back arrow */}
      <button
        onClick={() => navigate(-1)}
        className="absolute top-6 left-6 flex items-center justify-center w-10 h-10 text-cream hover:text-purple hover:bg-dark/50 rounded-lg transition-colors"
      >
        <ArrowLeft size={20} />
      </button>

      <div className="w-full max-w-[400px] flex flex-col items-center">
        {content}
      </div>
    </div>
  )
}

function LoginPage() {
  if (AUTH_PROVIDER === "supabase") {
    return (
      <Suspense fallback={null}>
        <LegacyLoginPage />
      </Suspense>
    )
  }
  return <ClerkLoginPage />
}

export default LoginPage
